/**
 * The Add-chart / Edit-chart modal: Explore, in a frame.
 *
 * The editor is not a copy of Explore - it **is** Explore: a second copy of
 * the app in a same-origin frame, started in builder mode (`state/embed.ts`)
 * on the chart's recipe. Anything you can build on Explore you can build here,
 * and it stays that way for free as Explore grows.
 *
 * The frame is its own app instance, so its state is its own. The Explore on
 * the page is never touched, and nothing on it reaches the builder - there is
 * no snapshot to take and nothing to give back. (Until 2026-10-05 the dialog
 * mounted Explore's components on Explore's own atoms and restored them on
 * close; every field the snapshot missed leaked, and Explore's data source
 * leaking into Add chart is what retired it.)
 *
 * The dialog keeps what belongs to the dashboard - the title, the pins,
 * Cancel and Save - and reads the recipe from the frame
 * (`builder-frame.ts`). A Catalog chart's thumbnail is taken inside the frame,
 * from the chart it is drawing, before the frame goes away.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import type { CatalogChart } from '../../api/types.ts';
import { chartOpenQuery, updateChartRecipe, refreshChart } from '../catalog/catalog-state.ts';
import { stripContext } from '../../state/presets.ts';
import { shipLabels } from '../../utils/ships.ts';
import { builderLaunchParams } from '../../state/embed.ts';
import { suggestTileTitle, tileExploreQuery } from './tile-spec.ts';
import { activeDashboard, type ContextPins, type DashboardTile } from './dashboard-state.ts';
import { isRef, LIVE_WINDOW_VAR, WINDOW_VAR, type TimeWindow, type VariableValues } from './variables.ts';
import { chartPinsShip, chartPinsTime } from '../catalog/chart-summary.ts';
import { BUILDER_CHANGE, type BuilderFrameApi } from './builder-frame.ts';
import type { SharedContext } from './tile-spec.ts';

export interface BuilderCommit {
  tileId: string | null;
  title: string;
  pins: ContextPins;
  /** Full query string, context included; the store strips what it does not pin. */
  query: string;
}

@customElement('chart-builder')
export class ChartBuilder extends LightElement {
  /** The tile being edited, or null when adding a new one. */
  @property({ attribute: false }) tile: DashboardTile | null = null;
  /** A Catalog chart being edited instead of a dashboard tile. */
  @property({ attribute: false }) catalogChart: CatalogChart | null = null;
  @property({ attribute: false }) shared!: VariableValues;

  @state() private chartTitle = '';
  @state() private pinShip = false;
  @state() private pinTime = false;
  @state() private saving = false;
  /** True once the frame's app has said it is there. */
  @state() private ready = false;

  /** Built once: a new `src` would reload the frame and lose the edit. */
  private frameSrc = '';
  private closing = false;

  /* ── Open / close lifecycle ───────────────────────────── */

  override connectedCallback(): void {
    super.connectedCallback();
    if (this.catalogChart) {
      this.chartTitle = this.catalogChart.title;
      this.pinShip = chartPinsShip(this.catalogChart);
      this.pinTime = chartPinsTime(this.catalogChart);
    } else if (this.tile) {
      this.chartTitle = this.tile.title;
      this.pinShip = !isRef(this.tile.bind.ship);
      this.pinTime = !isRef(this.tile.bind.window);
    }
    this.frameSrc = this.buildFrameSrc();
    window.addEventListener('message', this.onMessage);
    document.addEventListener('keydown', this.onKeydown);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('message', this.onMessage);
    document.removeEventListener('keydown', this.onKeydown);
  }

  /** The frame says its recipe may have changed: re-read the header's inputs. */
  private onMessage = (e: MessageEvent): void => {
    if (e.origin !== window.location.origin) return;
    if ((e.data as { type?: string } | null)?.type !== BUILDER_CHANGE) return;
    this.ready = true;
    this.requestUpdate();
  };

  private onKeydown = (e: KeyboardEvent): void => {
    // Enter is not a submit here: the frame is full of inputs where Enter
    // means something already. Escape inside the frame stays the frame's.
    if (e.key === 'Escape') this.cancel();
  };

  /* ── The frame ────────────────────────────────────────── */

  private frameApi(): BuilderFrameApi | null {
    const frame = this.querySelector<HTMLIFrameElement>('iframe.cbuilder-frame');
    try {
      return frame?.contentWindow?.__ndeBuilder ?? null;
    } catch {
      return null;
    }
  }

  /** The recipe the frame opens on, and the dashboard's windows for its source step. */
  private buildFrameSrc(): string {
    const params = builderLaunchParams(this.windows());
    const seed = new URLSearchParams(this.seedQuery());
    seed.delete('pc'); // tiles do not draw period comparisons
    for (const [k, v] of seed) params.set(k, v);
    // The page's development flags (`?ff=`), so the frame offers what it does.
    const ff = new URLSearchParams(window.location.search).get('ff');
    if (ff) params.set('ff', ff);
    params.set('tab', 'plot');
    return `/explore/plot?${params.toString()}`;
  }

  /** What the frame should show first: the chart being edited, or a fresh one. */
  private seedQuery(): string {
    if (this.catalogChart) return chartOpenQuery(this.catalogChart);
    if (this.tile) return tileExploreQuery(this.tile, this.shared as SharedContext);
    // A new chart starts where the dashboard is looking - its ship, and its
    // Time, or its Live window when the dashboard draws only Live.
    const params = new URLSearchParams();
    const [anchor, ...rest] = this.shared.ships;
    if (anchor) params.set('ship', anchor);
    if (rest.length) params.set('ships', rest.join(','));
    const w = this.windows();
    if (this.onlyLive() && w.livePreset) {
      params.set('ds', 'live');
      params.set('preset', w.livePreset);
    } else {
      const window = this.storedWindow(WINDOW_VAR);
      if (window?.preset) params.set('preset', window.preset);
      if (this.shared.start) params.set('start', this.shared.start);
      if (this.shared.end) params.set('end', this.shared.end);
    }
    return params.toString();
  }

  private storedWindow(id: string): TimeWindow | null {
    const v = activeDashboard()?.variables.find((x) => x.id === id);
    const value = v?.current ?? v?.default;
    return value && !Array.isArray(value) ? value : null;
  }

  /** The dashboard's Live window and Time, for switching source in the frame. */
  private windows(): { livePreset: string | null; historyStart: string | null; historyEnd: string | null } {
    // A Catalog chart has no dashboard, so no shared values.
    const shared = this.shared as VariableValues | undefined;
    const live = shared?.byId[LIVE_WINDOW_VAR];
    const livePreset = live && !Array.isArray(live) ? (live.preset ?? null) : null;
    return { livePreset: livePreset ?? '10m', historyStart: shared?.start || null, historyEnd: shared?.end || null };
  }

  /** Whether every chart on the dashboard reads a source without history. */
  private onlyLive(): boolean {
    const live = new Set((this.shared as SharedContext).liveSources ?? []);
    const tiles = activeDashboard()?.tabs.flatMap((t) => t.tiles).filter((t) => t.kind === 'chart') ?? [];
    return tiles.length > 0 && tiles.every((t) => live.has(t.source));
  }

  /* ── Close ────────────────────────────────────────────── */

  private cancel(): void {
    if (this.closing) return;
    this.closing = true;
    this.dispatchEvent(new CustomEvent('builder-close', { bubbles: true, composed: true }));
  }

  private async save(): Promise<void> {
    const api = this.frameApi();
    if (this.closing || !api) return;
    this.closing = true;
    this.saving = true;
    // The recipe, not the page it was built on: development flags stay out.
    const recipe = new URLSearchParams(api.query());
    recipe.delete('ff');
    const query = recipe.toString();
    if (this.catalogChart) {
      const updated = await updateChartRecipe(this.catalogChart, {
        query,
        title: this.chartTitle.trim() || this.catalogChart.title,
        pinShip: this.pinShip,
        pinTime: this.pinTime,
      });
      if (!updated) {
        this.closing = false;
        this.saving = false;
        return;
      }
      // From the frame's own chart, and before the frame goes: its requests
      // end with it. A failed capture leaves the old card preview visibly
      // marked as older than the edited chart.
      const stored = await api.capture(updated.id, updated.version).catch(() => false);
      if (stored) void refreshChart(updated.id);
      this.dispatchEvent(new CustomEvent('builder-close', { bubbles: true, composed: true }));
      return;
    }
    const detail: BuilderCommit = {
      tileId: this.tile?.id ?? null,
      title: this.chartTitle.trim() || suggestTileTitle(stripContext(query)),
      pins: { ship: this.pinShip, time: this.pinTime },
      query,
    };
    this.dispatchEvent(
      new CustomEvent<BuilderCommit>('builder-commit', { detail, bubbles: true, composed: true }),
    );
  }

  /** What each pin would freeze, so the choice is not made blind. */
  private pinShipSummary(api: BuilderFrameApi | null): string {
    const ships = api?.ships() ?? [];
    return ships.length ? shipLabels(ships) : 'no ship selected';
  }

  private pinTimeSummary(api: BuilderFrameApi | null): string {
    const preset = api?.preset();
    return preset && preset !== 'custom' ? preset : 'this exact window';
  }

  /* ── Render ───────────────────────────────────────────── */

  override render(): TemplateResult {
    const api = this.ready ? this.frameApi() : null;
    const canSave = !!api?.canSave() && !this.saving;
    const catalog = this.catalogChart !== null;
    const placeholder = api ? suggestTileTitle(stripContext(api.query())) : '';
    return html`
      <div class="cbuilder-scrim" @click=${() => this.cancel()}></div>
      <div class="cbuilder" data-tour="chart-builder" role="dialog" aria-modal="true" aria-label="Chart builder">
        <header class="cbuilder-head">
          <div class="cbuilder-heading">
            <h2>${this.tile || catalog ? 'Edit chart' : 'Add chart'}</h2>
            <input
              class="cbuilder-title"
              placeholder=${placeholder || 'Chart title'}
              .value=${this.chartTitle}
              aria-label="Chart title"
              @input=${(e: Event) => {
                this.chartTitle = (e.target as HTMLInputElement).value;
              }}
            />
          </div>
          <div class="cbuilder-head-actions">
            <label class="cbuilder-pin" title=${catalog
              ? 'Save this chart with the ships shown now'
              : 'Freeze this tile to the ships shown now'}>
              <input
                type="checkbox"
                .checked=${this.pinShip}
                @change=${(e: Event) => {
                  this.pinShip = (e.target as HTMLInputElement).checked;
                }}
              />
              <span>
                Pin ships
                <em>${this.pinShip ? this.pinShipSummary(api) : catalog ? 'follows the viewer' : 'follows the dashboard'}</em>
              </span>
            </label>
            <label class="cbuilder-pin" title=${catalog
              ? 'Save this chart with the time range shown now'
              : 'Freeze this tile to the time range shown now'}>
              <input
                type="checkbox"
                .checked=${this.pinTime}
                @change=${(e: Event) => {
                  this.pinTime = (e.target as HTMLInputElement).checked;
                }}
              />
              <span>
                Pin time range
                <em>${this.pinTime ? this.pinTimeSummary(api) : catalog ? 'follows the viewer' : 'follows the dashboard'}</em>
              </span>
            </label>
            <button class="dash-btn" @click=${() => this.cancel()}>Cancel</button>
            <button
              class="dash-btn is-primary"
              ?disabled=${!canSave}
              title=${canSave ? '' : 'Pick a ship and at least one series first'}
              @click=${() => void this.save()}
            >
              ${this.saving ? 'Saving…' : this.tile || catalog ? 'Save changes' : 'Add to dashboard'}
            </button>
          </div>
        </header>

        <div class="cbuilder-body">
          ${this.ready ? nothing : html`<div class="cbuilder-loading" aria-live="polite">Opening the editor…</div>`}
          <iframe class="cbuilder-frame" title="Chart editor" src=${this.frameSrc}></iframe>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'chart-builder': ChartBuilder;
  }
}
